import React, { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import FocusTrap from "focus-trap-react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUseCaseIdentities,
  IgnoreUseCaseforIdentity,
  fetchVectorUseCases,
} from "../../../../store/slices/policiesSlice";
import SearchBox from "../../../common/SearchBox/SearchBox";
import { FaTimes, FaEye } from "react-icons/fa";
import "./UseCaseIdentitiesModal.css";
import { Loading, Pagination, Badge } from "../../../common";
import { getInitials } from "../../../../utils/helpers";
import "../../identities/IdentityTable/IdentityTable.css";
import { TYPE_COLORS } from "../../../../utils/constants";
import { useTimezone } from "../../../../context/TimezoneContext";
import { formatDateByTimezone } from "../../../../utils/dateFormatter";
import { useTheme } from "../../../../context/ThemeContext";

const EMPTY_IDENTITIES = [];
const UseCaseIdentitiesModal = ({ isOpen, onClose, useCaseData }) => {
  // console.log("usecaseData",useCaseData);
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [localTotalIdentities, setLocalTotalIdentities] = useState(0);
  const itemsPerPage = 10;
  const { timezone } = useTimezone();
  const modalRef = useRef(null);
  const { isDark } = useTheme();

  useEffect(() => {
    if (useCaseData?.total_identities !== undefined) {
      setLocalTotalIdentities(useCaseData?.total_identities);
    }
  }, [useCaseData, isOpen]);

  const cacheKey = useCaseData
    ? `${useCaseData.usecase_name}-${useCaseData.identity_type}`
    : null;
  const identities =
    useSelector((state) =>
      cacheKey ? state?.policies?.identitiesByUseCase[cacheKey] : null,
    ) || EMPTY_IDENTITIES;
  const loading = useSelector((state) => state?.policies?.modalLoading);
  const { totalItems } = useSelector(
    (state) => state?.policies?.modalPagination,
  );

  useEffect(() => {
    if (isOpen && useCaseData) {
      dispatch(
        fetchUseCaseIdentities({
          useCaseName: useCaseData.usecase_name,
          identityType: useCaseData.identity_type,
          totalIdentities: useCaseData.total_identities,
          page: currentPage,
          limit: itemsPerPage,
          search: search,
        }),
      );
    }
  }, [isOpen, useCaseData, currentPage, search, dispatch]);

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      setSelectedIdentity(null);
    }
  }, [isOpen]);

  /*-----------------------------------------------*/
  /* ── Focus trap & background interaction ────── */
  useEffect(() => {
    if (!isOpen) return;

    const appContent = document.getElementById("app-content");
    const previousOverflow = document.body.style.overflow;
    const previousFocusedElement = document.activeElement;

    /**
     * Disable background interaction and keyboard tab focus.
     * Important: modal must be rendered outside #app-content using portal.
     */
    appContent?.setAttribute("inert", "");
    appContent?.setAttribute("aria-hidden", "true");

    document.body.style.overflow = "hidden";

    const handleEscape = (e) => {
      if (isOpen && e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      appContent?.removeAttribute("inert");
      appContent?.removeAttribute("aria-hidden");

      document.body.style.overflow = previousOverflow;
      document.body.style.overflow = "unset";

      document.removeEventListener("keydown", handleEscape);

      if (previousFocusedElement?.focus) {
        previousFocusedElement.focus();
      }
    };
  }, [isOpen, onClose]);


  useEffect(() => {
    if (!isOpen) setCurrentPage(1);
  }, [isOpen]);

  const filtered = useMemo(() => {
    return identities.filter(
      (id) =>
        id?.name?.toLowerCase().includes(search.toLowerCase()) ||
        id?.upn?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [identities, search]);
  const [selectedIdentity, setSelectedIdentity] = useState(null);

  if (!isOpen) return null;

  const displayTotal =
    totalItems !== undefined ? totalItems : localTotalIdentities;
  const totalPages = Math.ceil(displayTotal / itemsPerPage);

  const handleBackLog = () => {
    if (!selectedIdentity || !useCaseData) return;
    // console.log("selectedIdenityUsecaseflagged_id",useCaseData.flagged_id)
    // Dispatch the thunk
    // Note: verify if your identity object uses .id, .upn, or .principalID
    dispatch(
      IgnoreUseCaseforIdentity({
        identitityId: selectedIdentity.id,
        riskId: selectedIdentity.flagged_id, // Ensure this key exists in useCaseData
      }),
    ).then((result) => {
      if (!result.error) {
        // Close the details modal after success
        setLocalTotalIdentities((prev) => Math.max(0, prev - 1));
        setSelectedIdentity(null);

        dispatch(fetchVectorUseCases(useCaseData.vector_id));
        const isLastItemOnPage = filtered.length === 1 && currentPage > 1;
        const newPage = isLastItemOnPage ? currentPage - 1 : currentPage;

        if (isLastItemOnPage) {
          setCurrentPage(newPage);
        }
        dispatch(
          fetchUseCaseIdentities({
            useCaseName: useCaseData?.usecase_name,
            identityType: useCaseData?.identity_type,
            // Use updated count
            totalIdentities: localTotalIdentities - 1,
            page: newPage,
            limit: itemsPerPage,
          }),
        );
      }
    });
  };

  const handleClose = () => {
  dispatch(fetchVectorUseCases()); // Fetch the data
  onClose();                       // Call the original close function
};

  return createPortal(
    <div className={`usecase-identity-modal-overlay ${isDark ? 'dark dark-theme' : 'light light-theme'}`} onClick={onClose}>
      <FocusTrap
        active={isOpen}
        focusTrapOptions={{
          initialFocus: () =>
            modalRef.current?.querySelector(".modal-close") || modalRef.current,
          fallbackFocus: () => modalRef.current,
          returnFocusOnDeactivate: true,
          escapeDeactivates: false,
          clickOutsideDeactivates: false,
          allowOutsideClick: true,
        }}
      >
        <div ref={modalRef} className="modal modal-lg" tabIndex={-1} onClick={(e) => e.stopPropagation()}>
          <div className="usecase-modal-header">
            <div>
              <h2>{useCaseData?.usecase_name}</h2>
              <p>
                <span>{localTotalIdentities} </span>
                {useCaseData?.identity_type} Identities
              </p>
            </div>
            <div>
              <SearchBox
                value={search}
                onChange={setSearch}
                placeholder="Search..."
              />
            </div>
            <button className="modal-close" onClick={handleClose}>
              <FaTimes />
            </button>
          </div>

          <div className="usecase-identity-modal-body">
            {loading ? (
              <Loading text="loading vectors" />
            ) : (
              <>
                {filtered && filtered.length > 0 ? (
                  <div
                    className="identity-table-container"
                    role="region"
                    aria-label="Use case identities"
                  >
                    <table className="identity-table" role="table">
                      <thead>
                        <tr>
                          <th scope="col" className="th-identity">
                            Identity
                          </th>
                          <th scope="col" className="th-type">
                            Type
                          </th>
                          <th scope="col" className="th-age">
                            Age(days)
                          </th>
                          <th scope="col" className="th-status">
                            Created At
                          </th>
                          <th scope="col" className="th-actions">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered?.map((identity, idx) => {
                          const rowId = identity?.id || idx;
                          return (
                            <tr key={rowId} className="identity-row" role="row">
                              {/* Identity Column with Avatar */}
                              <td className="td-identity">
                                <div className="identity-cell">
                                  <div
                                    className="identity-avatar"
                                    style={{
                                      backgroundColor: `${TYPE_COLORS[identity?.type] || "#999"}15`,
                                      color:
                                        TYPE_COLORS[identity?.type] || "#999",
                                    }}
                                    aria-hidden="true"
                                  >
                                    {getInitials(identity?.name || "NA")}
                                  </div>

                                  <div className="identity-info">
                                    <div className="identity-name">
                                      {identity?.name || "Unnamed"}
                                    </div>
                                    <div className="identity-secondary">
                                      {identity?.upn || identity?.id || "N/A"}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* Type Column */}
                              <td className="td-type">
                                {/* Note: Ensure <Badge> component is imported */}
                                <Badge>{identity?.type || "unknown"}</Badge>
                              </td>

                              {/* Age Column */}
                              <td className="td-age">
                                <span className="cell-text">
                                  {identity?.age || "N/A"}
                                </span>
                              </td>

                              {/* Created At (mapped to status styling) */}
                              <td className="td-status">
                                <span className="cell-text">
                                  {identity?.created_at
                                    ? formatDateByTimezone(
                                      identity?.created_at,
                                      timezone,
                                      "N/A",
                                    )
                                    : "N/A"}
                                </span>
                              </td>

                              {/* Actions Column */}
                              <td className="td-actions">
                                <button
                                  className="action-btn"
                                  onClick={() => setSelectedIdentity(identity)}
                                  title="View Details"
                                  aria-label={`View details for ${identity?.name}`}
                                >
                                  <FaEye />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="usecase-modal-empty">
                    {search ? (
                      <p>No identities found matching "{search}"</p>
                    ) : (
                      <p>No identities found for this use case</p>
                    )}
                  </div>
                )}

                {!search && (
                  <div className="modal-pagination-wrapper">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      itemsPerPage={itemsPerPage}
                      totalItems={totalItems}
                      onPageChange={(page) => setCurrentPage(page)}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </FocusTrap>

      {/* MOVE DETAILS MODAL OUTSIDE FocusTrap BUT INSIDE PORTAL OVERLAY */}
      {selectedIdentity && (
        <div
          className="details-modal-overlay"
          onClick={() => setSelectedIdentity(null)}
        >
          <div
            className="details-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with a subtle bottom border */}
            <div className="details-header">
              <div className="header-left">
                <span className="header-badge">Security Analysis</span>
                <h3>Identity Details</h3>
              </div>
              <button
                className="close-btn"
                onClick={() => setSelectedIdentity(null)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="details-body">
              {/* Reason Section - Using a soft blue container */}
              <div className="info-block reason-block">
                <label className="info-label">Detection Reason</label>
                <div className="info-content">
                  {selectedIdentity.reason ||
                    "No specific reason provided for this detection."}
                </div>
              </div>

              {/* Remediation Section - Using a soft green container */}
              <div className="info-block remediation-block">
                <label className="info-label">Recommended Remediation</label>
                <div className="info-content">
                  {selectedIdentity.remediation ||
                    "No automated remediation data available."}
                </div>
              </div>
            </div>

            <div className="details-footer">
              <button
                className="info-action-btn warning"
                onClick={handleBackLog}
              >
                Add to Backlog
              </button>
              <button
                className="info-action-btn secondary"
                onClick={() => setSelectedIdentity(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default UseCaseIdentitiesModal;
